/**
 * meta/panel-ui.js — in-world panels you can actually press.
 *
 * `ui/panels.js` draws read-only canvas panels; the meta layer needs buttons
 * (buy a piece, equip a hat, claim a day), so this adds the missing half:
 * a row-based layout that records each row's pixel rectangle, and a pointer
 * system that turns a ray hit into the row id under it.
 *
 * One ray source per input mode, both ending in the same `select(id)`:
 *   VR      — from the hand's pose along its forward direction, on trigger down
 *   desktop — from the camera through the mouse, on click
 *
 * Panels register themselves, so adding a screen anywhere in the hub costs
 * nothing beyond building it.
 *
 * @module meta/panel-ui
 */

import * as THREE from 'three';
import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { input } from '../core/input.js';

// 340 left a set name about ten screen pixels tall at browse distance, and the
// texture was being minified on top of that. Every hard-coded pixel size below
// is expressed as a fraction of the canvas so this number can move freely.
const PX_PER_METRE = 512;

/** Every interactive panel currently in the scene. @type {Set<THREE.Mesh>} */
const panels = new Set();

const raycaster = new THREE.Raycaster();
const pointerNDC = new THREE.Vector2();
let sceneCamera = null;
let domElement = null;
let installed = false;

/** Palette shared by every meta panel so they read as one system. */
const STYLE = {
  bg: '#131a2f',
  header: '#ffd166',
  text: '#ffffff',
  dim: '#a6b2d0',
  row: 'rgba(255,255,255,0.085)',
  rowHover: 'rgba(255,209,102,0.20)',
  rowOwned: 'rgba(111,231,196,0.16)',
  rowDisabled: 'rgba(255,255,255,0.035)',
  owned: '#6fe7c4',
  // Out-of-season sets are most of the list, and the old #5a6480 on this navy
  // was under 3:1 — legible on a monitor, a grey smear through a headset.
  disabled: '#96a2c0',
  accent: 'rgba(255,209,102,0.55)',
  bad: '#ff8f8f',
};

function roundRect(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

/**
 * @typedef {Object} PanelRow
 * @property {string} [id] Omit for a non-interactive row.
 * @property {string} label
 * @property {string} [right] Right-aligned value (a cost, a rank, a count).
 * @property {'normal'|'owned'|'disabled'|'selected'} [state]
 * @property {string} [note] Small second line.
 */

/**
 * Build an interactive panel.
 *
 * @param {{ width?: number, height?: number, title?: string, subtitle?: string,
 *           rows?: PanelRow[], footer?: string, tabs?: Array<{id,label}>,
 *           activeTab?: string }} [opts]
 * @returns {THREE.Mesh} With `setContent`, `onSelect`, `dispose`.
 */
export function createButtonPanel(opts = {}) {
  const width = opts.width ?? 1.9;
  const height = opts.height ?? 1.3;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * PX_PER_METRE);
  canvas.height = Math.round(height * PX_PER_METRE);
  const ctx = canvas.getContext('2d');

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;

  const panel = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({ map: texture, transparent: true }),
  );
  panel.name = 'meta-panel';

  const state = {
    title: opts.title ?? '',
    subtitle: opts.subtitle ?? '',
    rows: opts.rows ?? [],
    footer: opts.footer ?? '',
    tabs: opts.tabs ?? [],
    activeTab: opts.activeTab ?? null,
  };

  /** id → {x, y, w, h} in canvas pixels, rebuilt on every draw. */
  let hitRects = [];
  let hovered = null;
  /** @type {(id: string) => void} */
  let onSelect = () => {};

  function draw() {
    const w = canvas.width;
    const h = canvas.height;
    hitRects = [];

    const corner = Math.round(h * 0.048);
    const inset = Math.max(2, Math.round(h * 0.0044));

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = STYLE.bg;
    roundRect(ctx, 0, 0, w, h, corner);
    ctx.fill();
    ctx.strokeStyle = STYLE.accent;
    ctx.lineWidth = Math.max(3, Math.round(h * 0.0087));
    roundRect(ctx, inset, inset, w - inset * 2, h - inset * 2, corner);
    ctx.stroke();

    const pad = Math.round(w * 0.035);
    let y = pad;

    if (state.title) {
      const size = Math.round(h * 0.075);
      ctx.font = `bold ${size}px "Trebuchet MS", sans-serif`;
      ctx.fillStyle = STYLE.header;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(state.title, pad, y);
      y += size * 1.15;
    }
    if (state.subtitle) {
      const size = Math.round(h * 0.045);
      ctx.font = `${size}px "Trebuchet MS", sans-serif`;
      ctx.fillStyle = STYLE.dim;
      ctx.fillText(state.subtitle, pad, y);
      y += size * 1.5;
    }

    if (state.tabs.length) {
      const tabH = Math.round(h * 0.075);
      let x = pad;
      const size = Math.round(tabH * 0.5);
      ctx.font = `bold ${size}px "Trebuchet MS", sans-serif`;
      for (const tab of state.tabs) {
        const tw = ctx.measureText(tab.label).width + tabH * 0.9;
        const active = tab.id === state.activeTab;
        ctx.fillStyle = active ? STYLE.rowHover : STYLE.row;
        roundRect(ctx, x, y, tw, tabH, Math.round(h * 0.0174));
        ctx.fill();
        ctx.fillStyle = active ? STYLE.header : STYLE.dim;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(tab.label, x + tw / 2, y + tabH / 2);
        hitRects.push({ id: `tab:${tab.id}`, x, y, w: tw, h: tabH });
        x += tw + pad * 0.4;
      }
      y += tabH + pad * 0.5;
    }

    const footerH = state.footer ? Math.round(h * 0.07) : 0;
    const available = h - y - pad - footerH;
    const count = Math.max(1, state.rows.length);
    const gap = Math.max(3, Math.round(h * 0.010));
    const rowH = Math.min(Math.round(h * 0.13), Math.floor(available / count) - gap);
    const rowCorner = Math.round(h * 0.0196);

    ctx.textBaseline = 'middle';
    for (const row of state.rows) {
      if (y + rowH > h - pad - footerH) break;
      const interactive = !!row.id;
      const isHover = interactive && hovered === row.id;

      let fill = STYLE.row;
      if (row.state === 'owned') fill = STYLE.rowOwned;
      else if (row.state === 'disabled') fill = STYLE.rowDisabled;
      else if (row.state === 'selected' || isHover) fill = STYLE.rowHover;

      ctx.fillStyle = fill;
      roundRect(ctx, pad, y, w - pad * 2, rowH, rowCorner);
      ctx.fill();

      if (isHover || row.state === 'selected') {
        ctx.strokeStyle = STYLE.accent;
        ctx.lineWidth = Math.max(2, Math.round(h * 0.0065));
        roundRect(ctx, pad, y, w - pad * 2, rowH, rowCorner);
        ctx.stroke();
      }

      // The row label is the thing the player is actually reading, so it takes
      // most of the row. Capped against the panel height too, or a one-row
      // panel ends up with a banner where a button should be.
      const labelSize = Math.min(
        Math.round(rowH * (row.note ? 0.54 : 0.62)),
        Math.round(h * 0.072),
      );
      const rowInk = row.state === 'disabled' ? STYLE.disabled
        : row.state === 'owned' ? STYLE.owned : STYLE.text;

      // Measure the value first and cap the label to what is left. At these
      // sizes a long challenge line would otherwise run straight through it.
      let rightW = 0;
      if (row.right) {
        ctx.font = `bold ${labelSize}px "Trebuchet MS", sans-serif`;
        rightW = ctx.measureText(row.right).width + pad * 0.6;
      }
      const labelMax = Math.max(pad, w - pad * 3 - rightW);

      ctx.font = `bold ${labelSize}px "Trebuchet MS", sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillStyle = rowInk;
      ctx.fillText(row.label, pad * 1.5, y + (row.note ? rowH * 0.34 : rowH / 2), labelMax);

      if (row.note) {
        ctx.font = `${Math.round(rowH * 0.26)}px "Trebuchet MS", sans-serif`;
        ctx.fillStyle = STYLE.dim;
        ctx.fillText(row.note, pad * 1.5, y + rowH * 0.755, labelMax);
      }

      if (row.right) {
        ctx.font = `bold ${labelSize}px "Trebuchet MS", sans-serif`;
        ctx.textAlign = 'right';
        ctx.fillStyle = row.state === 'disabled' ? STYLE.disabled
          : row.state === 'owned' ? STYLE.owned : STYLE.header;
        ctx.fillText(row.right, w - pad * 1.5, y + rowH / 2);
      }

      if (interactive) hitRects.push({ id: row.id, x: pad, y, w: w - pad * 2, h: rowH });
      y += rowH + gap;
    }

    if (state.footer) {
      ctx.font = `${Math.round(h * 0.042)}px "Trebuchet MS", sans-serif`;
      ctx.fillStyle = STYLE.dim;
      ctx.textAlign = 'center';
      ctx.fillText(state.footer, w / 2, h - pad - footerH / 2);
    }

    texture.needsUpdate = true;
  }

  draw();

  /** @param {Partial<typeof state>} patch */
  panel.setContent = (patch) => { Object.assign(state, patch); draw(); };
  /** @param {(id: string) => void} fn */
  panel.onSelect = (fn) => { onSelect = fn; };
  panel.getRowIds = () => hitRects.map((r) => r.id);

  /**
   * @param {THREE.Vector2} uv
   * @returns {string|null} The row id under this UV, if any.
   */
  panel.hitTest = (uv) => {
    const px = uv.x * canvas.width;
    // Canvas Y runs down, UV runs up.
    const py = (1 - uv.y) * canvas.height;
    for (const r of hitRects) {
      if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) return r.id;
    }
    return null;
  };

  panel.setHovered = (id) => {
    if (hovered === id) return;
    hovered = id;
    draw();
  };

  panel.select = (id) => { if (id) onSelect(id); };

  panel.dispose = () => {
    panels.delete(panel);
    texture.dispose();
    panel.material.dispose();
    panel.geometry.dispose();
  };

  panels.add(panel);
  return panel;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pointer system
// ─────────────────────────────────────────────────────────────────────────────

/** @returns {THREE.Mesh[]} Panels currently eligible for a hit. */
function activePanels() {
  return [...panels].filter((p) => p.visible && p.parent);
}

/**
 * Resolve a ray against the panels and act on it.
 * @param {'hover'|'select'} mode
 * @returns {{ panel: THREE.Mesh, id: string|null }|null}
 */
function castAndApply(mode) {
  const list = activePanels();
  if (!list.length) return null;

  const hits = raycaster.intersectObjects(list, false);
  if (!hits.length) {
    if (mode === 'hover') for (const p of list) p.setHovered(null);
    return null;
  }

  const hit = hits[0];
  const id = hit.uv ? hit.object.hitTest(hit.uv) : null;

  if (mode === 'hover') {
    for (const p of list) p.setHovered(p === hit.object ? id : null);
  } else if (id) {
    hit.object.select(id);
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'ui-click' });
  }
  return { panel: hit.object, id };
}

/** Aim the shared raycaster down a tracked hand. @param {'left'|'right'} side */
function aimFromHand(side) {
  const hand = input.getHand(side);
  if (!hand) return false;
  raycaster.set(hand.position, hand.direction.clone().normalize());
  return true;
}

/**
 * Install the pointer system. Idempotent.
 * @param {THREE.Camera} camera
 * @param {HTMLElement} element The renderer's DOM element.
 */
export function installPanelPointer(camera, element) {
  if (installed) return;
  installed = true;
  sceneCamera = camera;
  domElement = element;

  // ── VR: point and pull the trigger ──
  bus.on(EVENTS.INPUT_TRIGGER_DOWN, ({ side }) => {
    if (!input.isVR) return;
    if (aimFromHand(side)) castAndApply('select');
  });

  if (!domElement) return;

  // ── Desktop: mouse ──
  const toNDC = (event) => {
    const rect = domElement.getBoundingClientRect();
    pointerNDC.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointerNDC.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  };

  domElement.addEventListener('mousemove', (event) => {
    if (input.isVR || !activePanels().length) return;
    toNDC(event);
    raycaster.setFromCamera(pointerNDC, sceneCamera);
    castAndApply('hover');
  });

  domElement.addEventListener('click', (event) => {
    if (input.isVR || !activePanels().length) return;
    toNDC(event);
    raycaster.setFromCamera(pointerNDC, sceneCamera);
    castAndApply('select');
  });
}

/** Per-frame VR hover. Cheap: only runs with a visible panel and a tracked hand. */
export function updatePanelPointer() {
  if (!input.isVR || !activePanels().length) return;
  const side = input.activeSide || 'right';
  if (aimFromHand(side)) castAndApply('hover');
}

/** @returns {number} How many interactive panels exist — used by tests. */
export function panelCount() {
  return activePanels().length;
}
